import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SportSelector from '../components/SportSelector';
import PositionSelector from '../components/PositionSelector';
import GuardianManagement from '../components/GuardianManagement';
import PublicResumeSettings from '../components/PublicResumeSettings';
import ProfileImageUpload from '../components/ProfileImageUpload';
import FeatureGate from '../components/FeatureGate';
import UpgradeModal from '../components/UpgradeModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import supabase from '../supabase/supabase';

const { 
  FiUser, FiLock, FiBell, FiUsers, FiEye, FiSave, 
  FiPlus, FiX, FiCheckCircle, FiAlertCircle, FiGlobe, 
  FiTrendingUp, FiVideo, FiEdit3, FiTrash2, FiCreditCard, 
  FiZap, FiPlay, FiExternalLink, FiYoutube, FiLoader, 
  FiShield, FiAlertTriangle, FiInstagram, FiTwitter, FiMusic
} = FiIcons;

// Robust YouTube ID extraction supporting standard, shorts, embed, and shortened URLs
const extractYouTubeId = (url) => {
  if (!url) return null;
  const regExp = new RegExp(
    '(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?|shorts)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})',
    'i'
  );
  const match = url.match(regExp);
  return match ? match[1] : null;
};

function Settings() {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const { 
    subscriptionData, 
    isTrialActive, 
    hasActiveSubscription,
    getDaysLeftInTrial,
    canAccessPremiumFeatures,
  } = useSubscription();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    city: '',
    state: '',
    sport: '',
    position: '',
    graduation_year: '',
    height: '',
    weight: '',
    gpa: '',
    sat_score: '',
    act_score: '',
    class_rank: '',
    high_school: '',
    club_team: '',
    league: '',
    division: '',
    youtube_channel: '',
    profile_image: '',
    instagram: '',
    twitter: '',
    tiktok: '',
    philosophy_quote: ''
  });

  const [honors, setHonors] = useState([]);
  const [newHonor, setNewHonor] = useState('');
  
  const [courses, setCourses] = useState([]);
  const [newCourse, setNewCourse] = useState('');

  const [achievements, setAchievements] = useState([]);
  const [newAchievement, setNewAchievement] = useState({ achievement: '', year: '' });

  // Statistics State
  const [stats, setStats] = useState([]);
  const [newStat, setNewStat] = useState({ stat_name: '', stat_value: '', season: '' });

  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    url: '',
    thumbnail: '',
    duration: '',
    video_type: 'highlight',
    description: '',
  });

  const [privacySettings, setPrivacySettings] = useState({
    showContactInfo: true,
    showAcademicRecords: true,
    showAcademicHonors: true,
    showAdvancedCourses: true,
    showTeamInfo: true,
    showPhysicalStats: true,
    showPerformanceStats: true,
    showVideoContent: true,
    showSocialMedia: true,
  });

  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (success === 'true') {
      setMessage({ type: 'success', text: '🎉 Payment successful! Your Pro subscription is now active.' });
      setActiveTab('subscription');
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
    } else if (cancelled === 'true') {
      setMessage({ type: 'error', text: 'Payment was cancelled. You can try again anytime.' });
      setActiveTab('subscription');
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  }, [searchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    loadUserProfile();
  }, [user, getActiveUserId()]);

  useEffect(() => {
    if (activeTab === 'videos') loadVideos();
  }, [activeTab]);

  const loadUserProfile = async () => {
    try {
      const activeUserId = getActiveUserId();

      // Load Profile Data
      const { data, error } = await supabase.from('profiles').select('*').eq('user_id', activeUserId).single();
      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setProfileForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || user?.email || '',
          city: data.city || '',
          state: data.state || '',
          sport: data.sport || '',
          position: data.position || '',
          graduation_year: data.graduation_year || '',
          height: data.height || '',
          weight: data.weight || '',
          gpa: data.gpa || '',
          sat_score: data.sat_score || '',
          act_score: data.act_score || '',
          class_rank: data.class_rank || '',
          high_school: data.high_school || '',
          club_team: data.club_team || '',
          league: data.league || '',
          division: data.division || '',
          youtube_channel: data.youtube_channel || '',
          profile_image: data.profile_image || '',
          instagram: data.instagram || '',
          twitter: data.twitter || '',
          tiktok: data.tiktok || '',
          philosophy_quote: data.philosophy_quote || ''
        });

        if (data.privacy_settings) {
          setPrivacySettings({
            ...privacySettings,
            ...data.privacy_settings
          });
        }
      }

      // Load Stats
      const { data: statsData } = await supabase.from('stats').select('*').eq('user_id', activeUserId).order('created_at', { ascending: false });
      if (statsData) setStats(statsData);

      // Load Honors
      const { data: honorsData } = await supabase.from('academic_honors').select('*').eq('user_id', activeUserId);
      if (honorsData) setHonors(honorsData);

      // Load Courses
      const { data: coursesData } = await supabase.from('advanced_courses').select('*').eq('user_id', activeUserId);
      if (coursesData) setCourses(coursesData);

      // Load Achievements
      const { data: achData } = await supabase.from('achievements').select('*').eq('user_id', activeUserId).order('year', { ascending: false });
      if (achData) setAchievements(achData);

    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadVideos = async () => {
    try {
      setLoadingVideos(true);
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('videos').select('*').eq('user_id', activeUserId).order('created_at', { ascending: false });
      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load videos.' });
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      const { error } = await supabase.from('profiles').update({
        ...profileForm,
        updated_at: new Date().toISOString()
      }).eq('user_id', activeUserId);

      if (error) throw error;
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddStat = async () => {
    if (!newStat.stat_name.trim() || !newStat.stat_value.trim()) return;
    try {
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('stats').insert({
        user_id: activeUserId,
        stat_name: newStat.stat_name.trim(),
        stat_value: newStat.stat_value.trim(),
        season: newStat.season.trim()
      }).select().single();
      
      if (error) throw error;
      setStats([data, ...stats]);
      setNewStat({ stat_name: '', stat_value: '', season: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add stat.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleRemoveStat = async (id) => {
    try {
      const { error } = await supabase.from('stats').delete().eq('id', id);
      if (error) throw error;
      setStats(stats.filter(s => s.id !== id));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove stat.' });
    }
  };

  const handleAddHonor = async () => {
    if (!newHonor.trim()) return;
    try {
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('academic_honors').insert({
        user_id: activeUserId,
        honor: newHonor.trim()
      }).select().single();
      
      if (error) throw error;
      setHonors([...honors, data]);
      setNewHonor('');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add honor.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleRemoveHonor = async (id) => {
    try {
      const { error } = await supabase.from('academic_honors').delete().eq('id', id);
      if (error) throw error;
      setHonors(honors.filter(h => h.id !== id));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove honor.' });
    }
  };

  const handleAddCourse = async () => {
    if (!newCourse.trim()) return;
    try {
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('advanced_courses').insert({
        user_id: activeUserId,
        course: newCourse.trim()
      }).select().single();
      
      if (error) throw error;
      setCourses([...courses, data]);
      setNewCourse('');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add course.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleRemoveCourse = async (id) => {
    try {
      const { error } = await supabase.from('advanced_courses').delete().eq('id', id);
      if (error) throw error;
      setCourses(courses.filter(c => c.id !== id));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove course.' });
    }
  };

  const handleAddAchievement = async () => {
    if (!newAchievement.achievement.trim()) return;
    try {
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('achievements').insert({
        user_id: activeUserId,
        achievement: newAchievement.achievement.trim(),
        year: newAchievement.year
      }).select().single();
      
      if (error) throw error;
      setAchievements([data, ...achievements]);
      setNewAchievement({ achievement: '', year: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add achievement.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleRemoveAchievement = async (id) => {
    try {
      const { error } = await supabase.from('achievements').delete().eq('id', id);
      if (error) throw error;
      setAchievements(achievements.filter(a => a.id !== id));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove achievement.' });
    }
  };

  const handleSavePrivacy = async () => {
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      const { error } = await supabase.from('profiles').update({
        privacy_settings: privacySettings,
        updated_at: new Date().toISOString()
      }).eq('user_id', activeUserId);

      if (error) throw error;
      setMessage({ type: 'success', text: 'Privacy settings updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update privacy settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVideos = async () => {
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      const { error } = await supabase.from('profiles').update({
        youtube_channel: profileForm.youtube_channel,
        updated_at: new Date().toISOString()
      }).eq('user_id', activeUserId);

      if (error) throw error;
      setMessage({ type: 'success', text: 'Video settings updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update video settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddVideo = () => {
    setEditingVideoId(null);
    setVideoForm({
      title: '',
      url: '',
      thumbnail: '',
      duration: '',
      video_type: 'highlight',
      description: ''
    });
    setShowVideoModal(true);
  };

  const handleEditVideo = (video) => {
    setEditingVideoId(video.id);
    setVideoForm({
      title: video.title || '',
      url: video.url || '',
      thumbnail: video.thumbnail || '',
      duration: video.duration || '',
      video_type: video.video_type || 'highlight',
      description: video.description || ''
    });
    setShowVideoModal(true);
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      setSaving(true);
      const { error } = await supabase.from('videos').delete().eq('id', videoId);
      if (error) throw error;
      setVideos(videos.filter(v => v.id !== videoId));
      setMessage({ type: 'success', text: 'Video deleted successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete video.' });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUrlChange = (url) => {
    const updates = { url };
    // Auto-fill the thumbnail
    const ytId = extractYouTubeId(url);
    if (ytId) {
      updates.thumbnail = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
    }
    setVideoForm({ ...videoForm, ...updates });
  };

  const handleSaveVideo = async () => {
    if (!videoForm.title || !videoForm.url) {
      setMessage({ type: 'error', text: 'Please provide at least a title and URL.' });
      return;
    }
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      if (editingVideoId) {
        const { error } = await supabase.from('videos').update({
          ...videoForm,
          updated_at: new Date().toISOString()
        }).eq('id', editingVideoId);
        if (error) throw error;
        setVideos(videos.map(v => v.id === editingVideoId ? { ...v, ...videoForm } : v));
        setMessage({ type: 'success', text: 'Video updated successfully!' });
      } else {
        const { data, error } = await supabase.from('videos').insert({
          user_id: activeUserId,
          ...videoForm
        }).select();
        if (error) throw error;
        setVideos([...(data || []), ...videos]);
        setMessage({ type: 'success', text: 'Video added successfully!' });
      }
      setShowVideoModal(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save video.' });
    } finally {
      setSaving(false);
    }
  };

  const hasAccess = canAccessPremiumFeatures();

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FiUser, requiresAccess: false },
    { id: 'stats', label: 'Statistics', icon: FiTrendingUp, requiresAccess: true },
    { id: 'videos', label: 'Videos', icon: FiVideo, requiresAccess: true },
    { id: 'public-resume', label: 'Public Resume', icon: FiGlobe, requiresAccess: true },
    { id: 'subscription', label: 'Subscription', icon: FiCreditCard, requiresAccess: false },
    { id: 'guardians', label: 'Guardians', icon: FiUsers, requiresAccess: true },
    { id: 'privacy', label: 'Privacy', icon: FiEye, requiresAccess: true },
    { id: 'notifications', label: 'Notifications', icon: FiBell, requiresAccess: true },
    { id: 'security', label: 'Security', icon: FiLock, requiresAccess: false },
  ];

  const renderProfileSettings = () => (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Personal Information</h3>
        <div className="mb-8">
          <ProfileImageUpload 
            userId={getActiveUserId()}
            currentImageUrl={profileForm.profile_image}
            onImageUpdate={(url) => setProfileForm({ ...profileForm, profile_image: url })}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { key: 'first_name', label: 'First Name', type: 'text' },
            { key: 'last_name', label: 'Last Name', type: 'text' },
            { key: 'email', label: 'Contact Email', type: 'email' },
          ].map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
              <input
                type={field.type}
                value={profileForm[field.key]}
                onChange={(e) => setProfileForm({ ...profileForm, [field.key]: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {field.key === 'email' && (
                <p className="text-xs text-gray-500 mt-1">
                  This email will be shown on your public resume if contact info is enabled.
                </p>
              )}
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input
              type="text"
              value={profileForm.city}
              onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <input
              type="text"
              value={profileForm.state}
              onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., CA"
            />
          </div>
        </div>
      </div>

      {/* Social Media Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Social Media</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Username</label>
            <div className="relative">
              <SafeIcon icon={FiInstagram} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                value={profileForm.instagram} 
                onChange={(e) => setProfileForm({...profileForm, instagram: e.target.value})} 
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                placeholder="@username" 
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Twitter/X Username</label>
            <div className="relative">
              <SafeIcon icon={FiTwitter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                value={profileForm.twitter} 
                onChange={(e) => setProfileForm({...profileForm, twitter: e.target.value})} 
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                placeholder="@username" 
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">TikTok Username</label>
            <div className="relative">
              <SafeIcon icon={FiMusic} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                value={profileForm.tiktok} 
                onChange={(e) => setProfileForm({...profileForm, tiktok: e.target.value})} 
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                placeholder="@username" 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Athletic Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Athletic Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sport</label>
            <SportSelector 
              value={profileForm.sport} 
              onChange={(val) => setProfileForm({ ...profileForm, sport: val })} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
            <PositionSelector 
              sport={profileForm.sport}
              value={profileForm.position} 
              onChange={(val) => setProfileForm({ ...profileForm, position: val })} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Year</label>
            <input
              type="text"
              value={profileForm.graduation_year}
              onChange={(e) => setProfileForm({ ...profileForm, graduation_year: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Team & Program Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Team & Program Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">High School Name</label>
            <input
              type="text"
              value={profileForm.high_school}
              onChange={(e) => setProfileForm({ ...profileForm, high_school: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Club Team (if applicable)</label>
            <input
              type="text"
              value={profileForm.club_team}
              onChange={(e) => setProfileForm({ ...profileForm, club_team: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">League/Conference</label>
            <input
              type="text"
              value={profileForm.league}
              onChange={(e) => setProfileForm({ ...profileForm, league: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Division Level</label>
            <input
              type="text"
              value={profileForm.division}
              onChange={(e) => setProfileForm({ ...profileForm, division: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Personal Philosophy / Quote</label>
            <textarea
              value={profileForm.philosophy_quote}
              onChange={(e) => setProfileForm({ ...profileForm, philosophy_quote: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows="3"
              placeholder="e.g., I strive to be the hardest worker in the room and lead by example on and off the field."
            />
            <p className="text-xs text-gray-500 mt-1">This will be displayed as a quote on your public resume.</p>
          </div>
        </div>
      </div>

      {/* Physical & Academic Stats */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Physical & Academic Stats</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
            <input
              type="text"
              value={profileForm.height}
              onChange={(e) => setProfileForm({ ...profileForm, height: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 6'2&quot;"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Weight (lbs)</label>
            <input
              type="text"
              value={profileForm.weight}
              onChange={(e) => setProfileForm({ ...profileForm, weight: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 185"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">GPA</label>
            <input
              type="text"
              value={profileForm.gpa}
              onChange={(e) => setProfileForm({ ...profileForm, gpa: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 3.8"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SAT Score</label>
            <input
              type="text"
              value={profileForm.sat_score}
              onChange={(e) => setProfileForm({ ...profileForm, sat_score: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 1250"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ACT Score</label>
            <input
              type="text"
              value={profileForm.act_score}
              onChange={(e) => setProfileForm({ ...profileForm, act_score: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 28"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class Rank (optional)</label>
            <input
              type="text"
              value={profileForm.class_rank}
              onChange={(e) => setProfileForm({ ...profileForm, class_rank: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 15/450"
            />
          </div>
        </div>
      </div>

      {/* Academic Honors & Awards */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">Academic Honors & Awards</h3>
        <p className="text-sm text-gray-500 mb-4">List any academic honors, awards, or recognitions you've received</p>
        
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newHonor}
            onChange={(e) => setNewHonor(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddHonor()}
            placeholder="e.g., Honor Roll, National Honor Society"
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button 
            onClick={handleAddHonor}
            className="bg-primary text-white p-2 w-10 h-10 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {honors.map(h => (
            <div key={h.id} className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
              <span className="text-sm text-gray-700">{h.honor}</span>
              <button onClick={() => handleRemoveHonor(h.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Coursework */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">Advanced Coursework</h3>
        <p className="text-sm text-gray-500 mb-4">List AP, IB, Honors, or other advanced courses you've taken or are currently enrolled in</p>
        
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newCourse}
            onChange={(e) => setNewCourse(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCourse()}
            placeholder="e.g., AP Calculus, Honors Chemistry"
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button 
            onClick={handleAddCourse}
            className="bg-primary text-white p-2 w-10 h-10 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {courses.map(c => (
            <div key={c.id} className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
              <span className="text-sm text-gray-700">{c.course}</span>
              <button onClick={() => handleRemoveCourse(c.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Athletic Achievements */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">Athletic Achievements</h3>
        <p className="text-sm text-gray-500 mb-4">List your major athletic achievements and awards</p>
        
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newAchievement.achievement}
            onChange={(e) => setNewAchievement({ ...newAchievement, achievement: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && handleAddAchievement()}
            placeholder="Achievement (e.g., All-State First Team)"
            className="flex-[2] px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-w-0"
          />
          <select
            value={newAchievement.year}
            onChange={(e) => setNewAchievement({ ...newAchievement, year: e.target.value })}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-w-[100px]"
          >
            <option value="">Year</option>
            {Array.from({ length: 15 }, (_, i) => 2030 - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button 
            onClick={handleAddAchievement}
            className="bg-primary text-white p-2 w-10 h-10 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {achievements.map(a => (
            <div key={a.id} className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
              <span className="text-sm text-gray-700">
                {a.achievement} {a.year && <span className="text-gray-500">({a.year})</span>}
              </span>
              <button onClick={() => handleRemoveAchievement(a.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button 
          onClick={handleSaveProfile}
          disabled={saving}
          className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 font-semibold"
        >
          <SafeIcon icon={FiSave} className="w-5 h-5" />
          <span>{saving ? 'Saving...' : 'Save Profile'}</span>
        </button>
      </div>
    </div>
  );

  const renderStatsSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">Performance Statistics</h3>
        <p className="text-sm text-gray-500 mb-6">Add your key performance metrics, measurables, or season stats.</p>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stat Name *</label>
            <input
              type="text"
              value={newStat.stat_name}
              onChange={(e) => setNewStat({ ...newStat, stat_name: e.target.value })}
              placeholder="e.g., Points Per Game"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
            <input
              type="text"
              value={newStat.stat_value}
              onChange={(e) => setNewStat({ ...newStat, stat_value: e.target.value })}
              placeholder="e.g., 24.5"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Season/Year</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newStat.season}
                onChange={(e) => setNewStat({ ...newStat, season: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleAddStat()}
                placeholder="e.g., 2023-24"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button 
                onClick={handleAddStat}
                disabled={!newStat.stat_name || !newStat.stat_value}
                className="bg-primary text-white p-2 w-10 h-10 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0 disabled:opacity-50"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-3 mt-6">
          {stats.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No statistics added yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {stats.map(s => (
                <div key={s.id} className="flex justify-between items-center bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
                  <div>
                    <p className="font-bold text-lg text-primary">{s.stat_value}</p>
                    <p className="text-sm font-medium text-gray-700">{s.stat_name}</p>
                    {s.season && <p className="text-xs text-gray-500">{s.season}</p>}
                  </div>
                  <button onClick={() => handleRemoveStat(s.id)} className="text-gray-400 hover:text-red-500 transition-colors p-2">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderVideosSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-space-grotesk font-semibold text-secondary">YouTube Channel</h3>
          <button 
            onClick={handleAddVideo}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Video</span>
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Channel URL</label>
          <div className="relative">
            <SafeIcon icon={FiYoutube} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="url"
              value={profileForm.youtube_channel}
              onChange={(e) => setProfileForm({ ...profileForm, youtube_channel: e.target.value })}
              placeholder="https://youtube.com/@username"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="mt-8 flex justify-end pt-6 border-t border-gray-100">
          <button 
            onClick={handleSaveVideos}
            disabled={saving}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save YouTube Channel'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Video Management</h3>
        
        {loadingVideos ? (
          <div className="flex items-center justify-center py-12">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary animate-spin mx-auto" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <SafeIcon icon={FiVideo} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">No Videos Yet</h4>
            <button 
              onClick={handleAddVideo}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add Your First Video</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => {
              // Dynamically get thumbnail if missing in database
              const displayThumbnail = video.thumbnail || 
                (extractYouTubeId(video.url) ? `https://img.youtube.com/vi/${extractYouTubeId(video.url)}/hqdefault.jpg` : null);
                
              return (
                <motion.div 
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="relative h-40 bg-gray-200">
                    {displayThumbnail ? (
                      <img src={displayThumbnail} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <SafeIcon icon={FiPlay} className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    {video.duration && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button 
                        onClick={() => handleEditVideo(video)}
                        className="p-1 bg-white bg-opacity-80 rounded hover:bg-opacity-100 transition-opacity"
                      >
                        <SafeIcon icon={FiEdit3} className="w-4 h-4 text-gray-700" />
                      </button>
                      <button 
                        onClick={() => handleDeleteVideo(video.id)}
                        className="p-1 bg-white bg-opacity-80 rounded hover:bg-opacity-100 transition-opacity"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-800 mb-1 truncate">{video.title}</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded capitalize">
                        {video.video_type || 'Highlight'}
                      </span>
                      <a 
                        href={video.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-blue-700 transition-colors text-sm flex items-center space-x-1"
                      >
                        <span>View</span>
                        <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showVideoModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowVideoModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-space-grotesk font-bold text-secondary">
                      {editingVideoId ? 'Edit Video' : 'Add New Video'}
                    </h2>
                    <button onClick={() => setShowVideoModal(false)} className="text-gray-400 hover:text-gray-600">
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video Title *</label>
                      <input
                        type="text"
                        value={videoForm.title}
                        onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                        placeholder="e.g., 2024 Season Highlights"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video URL *</label>
                      <input
                        type="url"
                        value={videoForm.url}
                        onChange={(e) => handleVideoUrlChange(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail URL</label>
                        <input
                          type="url"
                          value={videoForm.thumbnail}
                          onChange={(e) => setVideoForm({ ...videoForm, thumbnail: e.target.value })}
                          placeholder="https://..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                        <input
                          type="text"
                          value={videoForm.duration}
                          onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })}
                          placeholder="e.g., 3:45"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video Type</label>
                      <select
                        value={videoForm.video_type}
                        onChange={(e) => setVideoForm({ ...videoForm, video_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="highlight">Highlight Reel</option>
                        <option value="skills">Skills Showcase</option>
                        <option value="game">Full Game</option>
                        <option value="training">Training</option>
                        <option value="interview">Interview</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={videoForm.description}
                        onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowVideoModal(false)}
                      className="px-6 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveVideo}
                      disabled={saving}
                      className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{saving ? 'Saving...' : (editingVideoId ? 'Update Video' : 'Add Video')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  const renderSubscriptionSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Subscription Plan</h3>
        
        {isTrialActive() && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-yellow-300" />
                <h4 className="text-lg font-bold">Pro Trial Active</h4>
              </div>
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                {getDaysLeftInTrial()} days left
              </div>
            </div>
            <p className="text-blue-100 mb-6">
              You have full access to all premium features during your trial period.
            </p>
            <button 
              onClick={() => setShowUpgradeModal(true)}
              className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Subscribe Now — Don't Lose Access
            </button>
          </div>
        )}

        {!isTrialActive() && !hasActiveSubscription() && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiLock} className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-800">Free Account</h4>
                <p className="text-sm text-gray-500">Trial expired</p>
              </div>
            </div>
            <button 
              onClick={() => setShowUpgradeModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center space-x-2"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              <span>Upgrade to Pro with Stripe</span>
            </button>
          </div>
        )}

        {hasActiveSubscription() && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-green-800">Pro Plan Active</h4>
                <p className="text-sm text-green-600">
                  {subscriptionData?.subscription_plan === 'pro_annual' ? 'Annual Billing' : 'Monthly Billing'}
                </p>
              </div>
            </div>
            <p className="text-green-700">You have full access to all premium features.</p>
          </div>
        )}

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiShield} className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-800 text-sm mb-1">Secure Payments via Stripe</h4>
              <p className="text-xs text-gray-600">
                All payments are processed securely by Stripe. We never store your credit card information.
              </p>
            </div>
          </div>
        </div>
      </div>

      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );

  const renderPrivacySettingsTab = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-6">Privacy Settings</h3>
      <p className="text-gray-600 text-sm mb-6">Control what information is visible on your public resume.</p>
      
      <div className="space-y-4">
        {[
          { key: 'showContactInfo', label: 'Contact Information', desc: 'Email address shown on public resume' },
          { key: 'showAcademicRecords', label: 'Academic Records', desc: 'GPA, SAT, ACT, and Class Rank' },
          { key: 'showAcademicHonors', label: 'Academic Honors', desc: 'Awards and honors' },
          { key: 'showAdvancedCourses', label: 'Advanced Courses', desc: 'AP, IB, and Honors courses' },
          { key: 'showTeamInfo', label: 'Team Information', desc: 'High school, club team, and division' },
          { key: 'showPhysicalStats', label: 'Physical Stats', desc: 'Height and weight' },
          { key: 'showPerformanceStats', label: 'Performance Stats', desc: 'Season statistics' },
          { key: 'showVideoContent', label: 'Video Content', desc: 'YouTube channel and highlight videos' },
          { key: 'showSocialMedia', label: 'Social Media', desc: 'Instagram, Twitter, and TikTok links' },
        ].map((item) => (
          <div key={item.key} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg">
            <div>
              <h4 className="font-semibold text-gray-800">{item.label}</h4>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={privacySettings[item.key] !== false} // Default to true if undefined
                onChange={(e) => setPrivacySettings({ ...privacySettings, [item.key]: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end pt-6 border-t border-gray-100">
        <button 
          onClick={handleSavePrivacy}
          disabled={saving}
          className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Privacy Settings'}</span>
        </button>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">Security</h3>
        <p className="text-gray-600 text-sm mb-6">Manage your account security settings.</p>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiLock} className="w-5 h-5 text-gray-500" />
            <div>
              <h4 className="font-semibold text-gray-800">Password</h4>
              <p className="text-sm text-gray-500">Manage your password via your email provider</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-red-200">
        <h3 className="text-xl font-space-grotesk font-semibold text-red-700 mb-2 flex items-center space-x-2">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
          <span>Danger Zone</span>
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        
        <div className="border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-800">Delete Account</h4>
            <p className="text-sm text-gray-500">Permanently delete your account and all associated data</p>
          </div>
          <button 
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold flex items-center space-x-2"
          >
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            <span>Delete Account</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-space-grotesk font-bold text-secondary mb-2">Account Settings</h1>
          <p className="text-gray-600">Manage your profile, subscription, and account preferences.</p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5" />
            <span>{message.text}</span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (!tab.requiresAccess || hasAccess) setActiveTab(tab.id);
                    }}
                    disabled={tab.requiresAccess && !hasAccess}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-white'
                        : tab.requiresAccess && !hasAccess
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={tab.icon} className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </div>
                    {tab.requiresAccess && !hasAccess && <SafeIcon icon={FiLock} className="w-3 h-3" />}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'profile' && renderProfileSettings()}
              {activeTab === 'stats' && <FeatureGate feature="Performance Statistics">{renderStatsSettings()}</FeatureGate>}
              {activeTab === 'videos' && <FeatureGate feature="Video Management">{renderVideosSettings()}</FeatureGate>}
              {activeTab === 'public-resume' && <FeatureGate feature="Public Resume"><PublicResumeSettings /></FeatureGate>}
              {activeTab === 'subscription' && renderSubscriptionSettings()}
              {activeTab === 'guardians' && <FeatureGate feature="Guardian Management"><GuardianManagement /></FeatureGate>}
              {activeTab === 'privacy' && <FeatureGate feature="Privacy Settings">{renderPrivacySettingsTab()}</FeatureGate>}
              {activeTab === 'security' && renderSecuritySettings()}
              
              {['notifications'].includes(activeTab) && (
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                  <SafeIcon icon={tabs.find((t) => t.id === activeTab)?.icon} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Coming Soon</h3>
                  <p className="text-gray-600">This feature is currently under development.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <DeleteAccountModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
      />
    </div>
  );
}

export default Settings;